import React, { createContext, useCallback, useContext, useState } from 'react';

/* ---------- ícones (linha, herdados do protótipo) ---------- */
const PATHS: Record<string, string> = {
  power: 'M12 2v10M18.4 6.6a9 9 0 1 1-12.77.04',
  reset: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  cam: 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z',
  chip: 'M9 9h6v6H9zM4 9h1M4 15h1M19 9h1M19 15h1M9 4v1M15 4v1M9 19v1M15 19v1M6 6h12v12H6z',
  cloud: 'M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z',
  film: 'M2 7h20M2 17h20M7 3v18M17 3v18M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
  wifi: 'M5 13a10 10 0 0 1 14 0M8.5 16.5a5 5 0 0 1 7 0M12 20h.01',
  spark: 'M12 3l1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2L12 3z',
  alert: 'M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  check: 'M20 6L9 17l-5-5',
  x: 'M18 6L6 18M6 6l12 12',
  plus: 'M12 5v14M5 12h14',
  edit: 'M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z',
  wa: 'M21 11.5a8.4 8.4 0 0 1-12.3 7.4L3 21l2.1-5.6A8.5 8.5 0 1 1 21 11.5z',
  money: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  percent: 'M19 5L5 19M6.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM17.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  doc: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35',
  img: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM21 15l-5-5L5 21',
  trash: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z',
};

export function Icon({ name, size = 16 }: { name: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size }}>
      <path d={PATHS[name] || ''} />
    </svg>
  );
}

/* ---------- toasts ---------- */
interface Toast { id: number; msg: string; kind: 'ok' | 'info' | 'warn' }
const ToastContext = createContext<(msg: string, kind?: Toast['kind']) => void>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((msg: string, kind: Toast['kind'] = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span className="ti">
              <Icon name={t.kind === 'ok' ? 'check' : t.kind === 'warn' ? 'alert' : 'spark'} size={14} />
            </span>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ---------- modal genérico ---------- */
interface ModalProps {
  title: string;
  desc?: string;
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  confirmClass?: string;
  busy?: boolean;
  wide?: boolean;
  children?: React.ReactNode;
}

export function Modal({
  title, desc, open, onClose, onConfirm, confirmText = 'Confirmar',
  confirmClass = 'btn-green', busy, wide, children,
}: ModalProps) {
  if (!open) return null;
  return (
    <div className="modal-back open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`}>
        <div className="modal-head">
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          {onConfirm && (
            <button className={`btn ${confirmClass}`} onClick={onConfirm} disabled={busy}>
              {busy ? <span className="spinner" /> : confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers de serviço ---------- */
export function serviceLabel(name: string): string {
  if (name.startsWith('button-cam-')) return `Botão câmera ${name.replace('button-cam-', '')}`;
  if (name.startsWith('ffmpeg-cam-')) {
    const m = name.match(/ffmpeg-cam-(\d+)-buffer-(\d+)/);
    if (m) return `Buffer ${m[2]} câmera ${m[1]}`;
  }
  if (name === 'process_queue') return 'Processamento / Upload';
  if (name === 'monitor_queue') return 'Fila de vídeos';
  if (name === 'apertai-stream') return 'Livestream';
  if (name === 'cloudflared-tunnel') return 'Túnel stream';
  if (name.includes('health')) return 'Health check local';
  return name;
}

export function serviceIcon(name: string): string {
  if (name.startsWith('button-cam-')) return 'spark';
  if (name.startsWith('ffmpeg-cam-')) return 'cam';
  if (name === 'process_queue') return 'cloud';
  if (name === 'monitor_queue') return 'film';
  if (name === 'apertai-stream' || name === 'cloudflared-tunnel') return 'wifi';
  return 'chip';
}

export const fmtAgo = (iso?: string | null): string => {
  if (!iso) return 'nunca';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return `${s}s atrás`;
  if (s < 5400) return `${Math.round(s / 60)}min atrás`;
  if (s < 90000) return `${Math.round(s / 3600)}h atrás`;
  return `${Math.round(s / 86400)}d atrás`;
};
